import {
  Component,
  Prop,
  h,
  Host,
  Element,
  Method,
  State,
  Listen,
} from '@stencil/core';

import { Variants, PrimaryColors } from '../../types';

import clsx from 'clsx';
import { IFocussable } from '../../types/IFocussable';
import { FocusUtils } from '../../utils/FocusUtils';

/**
 * A standard Link component.
 */
@Component({
  tag: 'md-link',
  styleUrl: 'md-link.css',
  shadow: true,
})
export class MdLink implements IFocussable {
  @Element() host: HTMLMdLinkElement;

  /**
   *  Sets the tabidx(tabindex) attribute on the link.
   *  (setting the value to be -1 removes the link from the tab order, so users can't focus it using the keyboard)
   */
  @Prop() tabidx = 0;

  /**
   * The URL that the link should navigate to.
   */
  @Prop() href = '#';

  /**
   * Whether the link should be displayed as disabled.
   */
  @Prop() disabled = false;

  /**
   * The target attribute specifies where to open the linked document.
   */
  @Prop() target: '_top' | '_blank' | '_self' | '_parent' = '_self';

  /**
   * The rel attribute specifies the relationship between the current document and the linked document.
   */
  @Prop() rel: 'noopener noreferrer' | 'noopener' | 'noreferrer' =
    'noopener noreferrer';

  /**
   * The color of the link or link as a button.
   */
  @Prop() color: PrimaryColors = 'blue';

  /**
   * Whether the link should be displayed with an underline.
   */
  @Prop() underline = true;

  /**
   * Whether the link should trigger a download when clicked.
   * - When `true`: downloads with original filename
   * - When string: downloads with specified filename
   * - When `false` or undefined: behaves as normal link
   */
  @Prop() download?: boolean | string = false;

  /**
   * Whether the link should be displayed inline.
   */
  @Prop() inline = false;

  /**
   * The variant of the link as button.
   */
  @Prop() variant: Variants = 'none';

  /**
   * Whether the link should be used as a button or as a hyperlink.
   */
  @Prop() as: 'a' | 'button' = 'a';

  /**
   * The accessibility label of the link.
   */
  @Prop() accessibilityLabel?: string;

  /**
   * Tracks if the button is focused
   */
  @State() isFocused = false;

  private focusableElement: HTMLElement;

  /**
   * Set Focus on the Link
   */

  @Method()
  async setFocus() {
    const { focusableElement, disabled, tabidx } = this;
    this.isFocused = FocusUtils.setFocus(focusableElement, disabled, tabidx);
  }

  /**
   * Add a listener for the blur event
   *
   */
  @Listen('blur')
  onBlurListener() {
    const element = this.host;
    if (document.activeElement !== element) {
      this.isFocused = false;
    }
  }

  render() {
    const hasIconStart = !!this.host.querySelector('[slot="icon-start"]');
    const hasIconEnd = !!this.host.querySelector('[slot="icon-end"]');

    const downloadAttr = typeof this.download === 'string' 
      ? this.download 
      : this.download ? '' : undefined;

    return (
      <Host
        role="link"
        class={{ 'md-link--inline': this.inline }}
      >
        <a
          href={this.href || '#'}
          ref={el => (this.focusableElement = el)}
          target={this.target === '_self' ? undefined : this.target}
          rel={this.target === '_blank' ? this.rel : undefined}
          download={downloadAttr}
          aria-label={this.accessibilityLabel}
          aria-disabled={this.disabled ? 'true' : undefined}
          tabIndex={this.disabled ? -1 : this.tabidx}
          part="md-link"
          class={clsx(
            'md-link',
            this.inline && 'md-link--inline',
            this.variant && this.as === 'button' ? `is-${this.variant}` : null,
            { 'is-disabled': this.disabled },
            { focusable: this.isFocused },
            this.color && this.as === 'button'
              ? `md-link--button--${this.color}`
              : `md-link--${this.color}`,
            this.as === 'button' && 'md-link--button',
          )}
        >
          {/**
           * Slot for the start icon.
           ** Insert an icon component  that you want to display
           * at the start of the link.
           */}
          {hasIconStart && <slot name="icon-start"></slot>}
          <span
            class={clsx(
              'md-link__text',
              this.underline && this.as !== 'button'
                ? 'md-link--underline'
                : null,
            )}
          >
            <slot></slot>
          </span>
          {/**
           * Slot for the end icon.
           ** Insert an icon component  that you want to display
           * at the end of the link.
           */}
          {hasIconEnd && <slot name="icon-end"></slot>}
        </a>
      </Host>
    );
  }
}
